import { readonly, ref } from 'vue'
import type { Ref } from 'vue'

export type PriorityGroupBy = 'SLA_STATE' | 'STAGE' | 'SITUATION'

export const PRIORITY_GROUP_BY_OPTIONS: { value: PriorityGroupBy; label: string }[] = [
  { value: 'SLA_STATE', label: 'Estado do SLA' },
  { value: 'STAGE', label: 'Etapa' },
  { value: 'SITUATION', label: 'Situação' }
]

export const PRIORITY_DEFAULT_GROUP_BY: PriorityGroupBy = 'SLA_STATE'

/** Preferência de EXIBIÇÃO por usuário (DO-19). Filtro nenhum vai para o storage. */
export const priorityGroupByStorageKey = (userId: number): string =>
  `priority-payments:group-by:${userId}`

/**
 * Id 0 = ainda sem usuário (`getUserId` antes do login): ler ou gravar aí
 * criaria uma chave compartilhada entre quem usar o navegador.
 */
const hasUser = (userId: number): boolean => userId > 0

const isGroupBy = (value: unknown): value is PriorityGroupBy =>
  PRIORITY_GROUP_BY_OPTIONS.some((option) => option.value === value)

/** Aba anônima, cookies bloqueados e storage cheio lançam: a tela usa o padrão. */
export function readPriorityGroupBy(userId: number): PriorityGroupBy {
  if (!hasUser(userId)) return PRIORITY_DEFAULT_GROUP_BY
  try {
    const stored = localStorage.getItem(priorityGroupByStorageKey(userId))
    return isGroupBy(stored) ? stored : PRIORITY_DEFAULT_GROUP_BY
  } catch {
    return PRIORITY_DEFAULT_GROUP_BY
  }
}

/** Um valor só para Lista e Kanban; a Tabela ignora (é plana). */
export function usePriorityGrouping(userId: number): {
  groupBy: Readonly<Ref<PriorityGroupBy>>
  setGroupBy: (value: PriorityGroupBy) => void
} {
  const groupBy = ref<PriorityGroupBy>(readPriorityGroupBy(userId))

  function setGroupBy(value: PriorityGroupBy): void {
    groupBy.value = value
    if (!hasUser(userId)) return
    try {
      localStorage.setItem(priorityGroupByStorageKey(userId), value)
    } catch {
      // Preferência é conveniência: perder a gravação não derruba a tela.
    }
  }

  return { groupBy: readonly(groupBy), setGroupBy }
}
